
import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown } from "lucide-react";

const useOutsideClick = (ref, callback) => {
    const handleClick = useCallback((e) => {
        if (ref.current && !ref.current.contains(e.target)) {
            callback();
        }
    }, [ref, callback]);

    useEffect(() => {
        document.addEventListener("mousedown", handleClick);
        return () => {
            document.removeEventListener("mousedown", handleClick);
        };
    }, [handleClick]);
};

const SearchableSelector = ({
    items,
    selectedItem,
    selectionableItems = null,
    itemIdKey = "id",
    itemNameKey = "name",
    onChange = () => {},
    placeholder = "Search and select an item",
    styles = {
        container: "",
        input: "",
        dropdown: "",
        item: "",
    },
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [focusedIndex, setFocusedIndex] = useState(0);
    const containerRef = useRef(null);

    useOutsideClick(containerRef, () => setIsOpen(false));

    const handleSelect = (item) => {
        if (selectedItem !== item[itemIdKey]) {
            onChange(item[itemIdKey]);
            setIsOpen(false);
            setFocusedIndex(0);
        }
    };

    const handleRemove = () => {
        onChange(null);
        setIsOpen(false);
    };

    const filteredItems = items
        .filter(item => !selectionableItems || selectionableItems.includes(item[itemIdKey]))
        .filter(item => item[itemNameKey]?.toLowerCase().includes(query.toLowerCase()));

    const handleKeyboardNavigation = (e) => {
        if (filteredItems.length > 0) {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setFocusedIndex((prevIndex) => (prevIndex + 1) % filteredItems.length);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setFocusedIndex((prevIndex) => (prevIndex - 1 + filteredItems.length) % filteredItems.length);
                    break;
                case 'Enter':
                    e.preventDefault();
                    handleSelect(filteredItems[focusedIndex]);
                    break;
                default:
                    break;
            }
        }
    };

    return (
        <div className="relative" ref={containerRef}>
            <div
                className={`border border-gray-300 rounded-sm flex items-center cursor-pointer focus-style ${styles.container || ""}`}
                onClick={() => setIsOpen(!isOpen && !selectedItem)}
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        setIsOpen(!isOpen && !selectedItem);
                    } else if (selectedItem && (e.key === 'Delete' || e.key === 'Backspace')) {
                        e.preventDefault();
                        handleRemove();
                    }
                }}
            >
                {selectedItem ? (
                    <div key={selectedItem} className={`bg-blue-100 text-nowrap text-blue-700 px-2 py-1 rounded-md flex items-center gap-1 ${styles.tag || ""}`}>
                        {(() => {
                            const item = items.find(i => i[itemIdKey] === selectedItem);
                            return item ? item[itemNameKey] : selectedItem;
                        })()}                         
                        <button onClick={(e) => { e.stopPropagation(); handleRemove(); }}>
                            ×
                        </button>
                    </div>
                ) : (
                    <>
                        <input
                            type="text"
                            className={`border-none outline-none flex-grow ${styles.input || ""}`}
                            readOnly
                            placeholder={placeholder}
                        />
                        <ChevronDown size={20} className="ml-2" />
                    </>
                )}
            </div>

            {isOpen && (
                <div
                    className={`absolute bg-white border border-gray-300 rounded-sm p-2 mt-1 w-full z-10 ${styles.dropdown || ""}`}
                    style={{ minWidth: '400px', whiteSpace: 'nowrap' }}
                    onKeyDown={handleKeyboardNavigation}
                >
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className={`border border-gray-300 p-1 mb-2 w-full ${styles.input || ""}`}
                        placeholder="Search..."
                    />
                    <ul className="max-h-60 overflow-y-auto overflow-x-hidden">
                        {filteredItems.length > 0 ? (
                            filteredItems.map((item, index) => (
                                <li
                                    key={item[itemIdKey]}
                                    className={`hover:bg-gray-100 p-1 cursor-pointer ${styles.item || ""} 
                                    ${index === focusedIndex ? 'bg-blue-100' : ''}`}
                                    onClick={() => handleSelect(item)}
                                >
                                    {item[itemNameKey]}
                                </li>
                            ))
                        ) : (
                            <li className="text-gray-500 p-1">No items found</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

SearchableSelector.propTypes = {
    items: PropTypes.array.isRequired,
    selectedItem: PropTypes.string,
    selectionableItems: PropTypes.array,
    onChange: PropTypes.func,
    itemIdKey: PropTypes.string,
    itemNameKey: PropTypes.string,
    placeholder: PropTypes.string,
    styles: PropTypes.object,
};

export default SearchableSelector;